import React from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { SchoolDashboard } from "../../common/api";
import { LessonImage } from "../../common/LessonImage";
import { COLOR_TEXT_PRIMARY, COLOR_TEXT_SECONDARY } from "../../common/colors";
import { THUMBNAIL_BORDER_RADIUS } from "../../common/constants";
import { SPACE_MD } from "../../common/spacing";
import {
  formatIsoDateAsLocalTimezoneDate,
  formatIsoDateAsLocalTimezoneTime,
} from "./common";
import { MetricsCard, MetricsRow } from "./MetricsCard";
import { LessonExecutionStatusBadge } from "./LessonExecutionStatusBadge";
import {
  ActivityTable,
  CompletedFilter,
  SecondaryCell,
  SectionHeader,
  SectionTitle,
} from "./DashboardStyles";

const LessonRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${SPACE_MD}px;
  padding: 6px 0;
  font-size: 13px;
  font-weight: 500;
  color: ${COLOR_TEXT_PRIMARY};
`;

const LessonIcon = styled.div`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: ${THUMBNAIL_BORDER_RADIUS}px;
  overflow: hidden;
`;

const LessonCount = styled.span`
  margin-left: auto;
  color: ${COLOR_TEXT_SECONDARY};
`;

const TopLessonsList = ({
  title,
  countLabel,
  lessons,
  imageAlt = "Lesson cover image",
}: {
  title: string;
  countLabel: string;
  lessons: [{ title: string }, string, number][];
  imageAlt?: string;
}) => (
  <Col xs={12} sm={6}>
    <SectionHeader>
      <SectionTitle>{title}</SectionTitle>
      {countLabel}
    </SectionHeader>
    <ul className="list-unstyled m-0">
      {lessons.map(([lesson, cover_asset, cnt], index) => (
        <LessonRow key={index}>
          <LessonIcon>
            <LessonImage
              thumbSize="small_cover_web"
              url={cover_asset}
              alt={imageAlt}
            />
          </LessonIcon>
          {lesson.title}
          <LessonCount>{cnt}</LessonCount>
        </LessonRow>
      ))}
    </ul>
  </Col>
);

export const GenericAnalyticsTab = ({
  dashboard,
  navigate,
}: {
  dashboard: SchoolDashboard &
    (
      | { is_at_school_level: true }
      | { is_at_teacher_level: true }
      | { is_at_school_group_level: true }
    );
  navigate: (string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      {"data_download_url" in dashboard && dashboard.data_download_url && (
        <div className="d-flex justify-content-end mb-3">
          <a
            className="btn btn-outline-dark btn-sm"
            href={dashboard.data_download_url}
          >
            {t("dashboard_export")}
          </a>
        </div>
      )}
      <MetricsRow>
        <MetricsCard
          label={t("dashboard_active_users")}
          value={t("intcomma", {
            val: dashboard.student_profile_cnt,
          })}
          // At school level the other three cards are cumulative and this one isn't, so it says
          // what it counts.
          tooltip={
            dashboard.is_at_school_level
              ? t("dashboard_active_users_tooltip")
              : undefined
          }
        />
        <MetricsCard
          label={t("dashboard_lessons_completed")}
          value={t("intcomma", {
            val: dashboard.lessons_completed_cnt,
          })}
        />
        <MetricsCard
          label={t("dashboard_hours_of_tutoring")}
          value={t("intcomma", {
            val: dashboard.time_spent_hours,
          })}
        />
        {!dashboard.is_at_teacher_level && (
          <MetricsCard
            label={t("dashboard_tutoring_value")}
            value={
              "$" +
              t("intcomma", {
                val: dashboard.tutoring_value_dollars,
              })
            }
          />
        )}
      </MetricsRow>
      <Row className="g-4 mt-1">
        <TopLessonsList
          title={t("dashboard_top_math_lessons")}
          countLabel={t("dashboard_top_lessons_completed")}
          lessons={dashboard.top_math_completions}
        />
        <TopLessonsList
          title={t("dashboard_top_reading_lessons")}
          countLabel={t("dashboard_top_lessons_completed")}
          lessons={dashboard.top_reading_completions}
        />
        <TopLessonsList
          title={t("dashboard_other_popular_lessons")}
          countLabel={t("dashboard_top_lessons_completed")}
          lessons={dashboard.top_other_completions}
        />
        <TopLessonsList
          title={t("dashboard_top_courses")}
          countLabel={t("dashboard_column_lesson_completions")}
          lessons={dashboard.top_units}
          imageAlt="Unit cover image"
        />
      </Row>
      {dashboard.lesson_executions && (
        <div className="mt-5">
          <SectionHeader>
            <SectionTitle>
              {t("dashboard_recent_math_and_reading_activity")}
            </SectionTitle>
          </SectionHeader>
          <ActivityTable>
            <thead>
              <tr>
                <th scope="col">{t("dashboard_column_date")}</th>
                <th scope="col">{t("dashboard_column_time")}</th>
                <th scope="col">{t("dashboard_column_student_name")}</th>
                <th scope="col">{t("dashboard_column_lesson_title")}</th>
                <th scope="col">{t("dashboard_column_time_in_lesson")}</th>
                <th style={{ width: "1%" }} scope="col">
                  {t("dashboard_column_completed")}{" "}
                  {"show_all_lesson_executions" in dashboard && (
                    <CompletedFilter
                      showAll={dashboard.show_all_lesson_executions}
                      toggleUrl={
                        dashboard.lesson_executions_filtered_the_other_way_url
                      }
                    />
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {dashboard.lesson_executions.map((execution, index) => (
                <tr key={index}>
                  <SecondaryCell>
                    {formatIsoDateAsLocalTimezoneDate(
                      execution.execution_date_iso
                    )}
                  </SecondaryCell>
                  <SecondaryCell>
                    {formatIsoDateAsLocalTimezoneTime(
                      execution.execution_date_iso
                    )}
                  </SecondaryCell>
                  <td>
                    <a
                      href={execution.student_url}
                      onClick={() => navigate(execution.student_url)}
                    >
                      {execution.student_name}
                    </a>
                  </td>
                  <td>
                    {execution.execution_url ? (
                      <a
                        href={execution.execution_url}
                        onClick={() => navigate(execution.execution_url)}
                      >
                        {execution.lesson_title}
                      </a>
                    ) : (
                      execution.lesson_title
                    )}
                  </td>
                  <SecondaryCell>{execution.execution_length}</SecondaryCell>
                  <td>
                    <LessonExecutionStatusBadge
                      isComplete={execution.is_complete}
                      isGood={execution.is_good}
                      isBad={execution.is_bad}
                      executionUrl={execution.execution_url}
                      onClick={(url) => navigate(url)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </ActivityTable>
        </div>
      )}
    </>
  );
};
